import { Copy } from "lucide-react";
import { handleCopy } from "@/utils/clipboard";

export type PromoEntry = {
	label: string;
	code: string;
	expiresAt: string;
};

const PROMO_ENTRIES: PromoEntry[] = [
	{ label: "Bilety na Roadshow -25%", code: "ROAD25", expiresAt: "2026-04-09" },
	{
		label: "Abonament AI i cyberbezpieczeństwo -20%",
		code: "CYBER20",
		expiresAt: "2026-04-10",
	},
	{
		label: "Abonament HR, Kadry, Płace, Prawo, Pracy -15%",
		code: "KADRY15",
		expiresAt: "2026-04-11",
	},
	{
		label: "Abonament Kompetencje Miękkie -20%",
		code: "MIEKKIE20",
		expiresAt: "2026-04-12",
	},
	{
		label: "Kurs Specjalista ds. Kadr i Płac -20%",
		code: "KURS20",
		expiresAt: "2026-04-13",
	},
	{
		label: "Abonament Prawo, Podatki, Rachunkowość -25%",
		code: "PODATKI25",
		expiresAt: "2026-04-14",
	},
];

/** After April 14, 2026 (end of day) the box is hidden. */
const CAMPAIGN_EXPIRES = "2026-04-15T00:00:00";

function formatExpiryLabel(isoDate: string): string {
	const d = new Date(`${isoDate}T12:00:00`);
	const day = String(d.getDate()).padStart(2, "0");
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const year = d.getFullYear();
	return `do ${day}.${month}.${year}`;
}

export type PromotionalCodesBoxViewProps = {
	entries: PromoEntry[];
	onCopy: (code: string) => void;
};

/**
 * Presentational layer — tweak layout / Tailwind classes here without touching data or expiry logic.
 */
export function PromotionalCodesBoxView({
	entries,
	onCopy,
}: PromotionalCodesBoxViewProps): JSX.Element {
	const now = new Date();
	const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

	return (
		<div className="px-[18px] py-[15px] rounded-[8px] mb-[15px] text-[#000810] bg-[#f0f9ff] border border-[#5b7a89]">
			<p className="ml-[14px] mb-3 text-[14px] font-semibold text-[#1a4a5c]">
				Kody promocyjne
			</p>
			<ul className="space-y-3 ml-[14px] mr-0">
				{entries.map((entry) => {
					const expired = todayIso > entry.expiresAt;
					return (
						<li
							key={entry.code}
							className={`text-[14px] border-b border-[#c8dce8] pb-3 last:border-b-0 last:pb-0 ${expired ? "opacity-50" : ""}`}
						>
							<div className="text-[#19191B] mb-1">{entry.label}</div>
							<div className="flex flex-wrap items-center gap-2 text-[#638595]">
								<span className="text-[13px]">{formatExpiryLabel(entry.expiresAt)}</span>
								<span className="text-[#637381]">·</span>
								<button
									type="button"
									aria-label={`Skopiuj kod ${entry.code}`}
									className="font-bold cursor-pointer inline-flex items-center gap-1.5 text-[#0d5c7a] hover:underline"
									onClick={async () => {
										await onCopy(entry.code);
									}}
								>
									<span>{entry.code}</span>
									<Copy className="w-4 h-4 shrink-0" />
								</button>
							</div>
						</li>
					);
				})}
			</ul>
			<div className="ml-[14px] mt-3 pt-3 border-t border-[#c8dce8] text-[13px] text-[#638595]">
				<span>Kod wpisz w koszyku przy zakupach na </span>
				<a
					className="underline font-bold tracking-wide text-[#0d5c7a]"
					href="https://www.akademialtca.pl"
					rel="noreferrer"
					target="_blank"
				>
					www.akademialtca.pl
				</a>
			</div>
		</div>
	);
}

export function PromotionalCodesBox(): JSX.Element | null {
	const now = new Date();
	if (now >= new Date(CAMPAIGN_EXPIRES)) {
		return null;
	}

	return (
		<PromotionalCodesBoxView
			entries={PROMO_ENTRIES}
			onCopy={handleCopy}
		/>
	);
}
